import React from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import { Card } from '@apigrowfinance/pbolt-card';
import CurrencyFormatter, {
    formatCurrency,
} from '@apigrowfinance/pbolt-currency-formatter';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import GenericLoadingText from '@apigrowfinance/pbolt-generic-loading-text';
import RowItem from '@apigrowfinance/pbolt-row-item';
import { RiCoinLine } from 'react-icons/ri';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage, useIntl } from 'react-intl';

import { Bucket } from '@clients/domains/delinquency/delinquencyBuckets';
import DelinquencyChips from '@components/delinquency/DelinquencyChips';
import { useAccount } from '@contexts/AccountProvider';

export type DelinquencyHistoryProps = {
    delinquencyBuckets: Bucket[];
    isLoading?: boolean;
    fetchNextPage: () => void;
    hasNextPage: boolean;
};

const DelinquencyHistory = ({
    delinquencyBuckets,
    isLoading = false,
    fetchNextPage,
    hasNextPage,
}: DelinquencyHistoryProps) => {
    const { program } = useAccount();
    const { formatNumber } = useIntl();

    // Add some empty rows for the loader
    if (isLoading) {
        delinquencyBuckets = [
            {
                delinquencyBucket_id: -1,
            } as Bucket,
            {
                delinquencyBucket_id: -2,
            } as Bucket,
            {
                delinquencyBucket_id: -3,
            } as Bucket,
        ];
    }

    return (
        <>
            <Box sx={{ mb: 2 }}>
                <Typography variant="overline">
                    {isLoading ? (
                        <Skeleton
                            data-testid="cycles-title-skeleton"
                            width={120}
                        />
                    ) : (
                        <FormattedMessage
                            id="DelinquencyHistory.title"
                            defaultMessage="Overdue balances"
                        />
                    )}
                </Typography>
            </Box>
            <Card>
                <InfiniteScroll
                    dataLength={delinquencyBuckets?.length}
                    next={fetchNextPage}
                    hasMore={hasNextPage}
                    loader={
                        <GenericLoadingText
                            displayText={
                                <FormattedMessage
                                    id="AccountTransactions.Loading"
                                    defaultMessage="Loading..."
                                />
                            }
                            showLoader
                        />
                    }>
                    {delinquencyBuckets?.map((bucket) => {
                        return (
                            <RowItem
                                testId="cycle-row"
                                isLoading={isLoading}
                                key={bucket.delinquencyBucket_id}
                                avatar={<RiCoinLine />}
                                primaryTitle={
                                    <FormattedMessage
                                        defaultMessage="Cycle {cycleNumber}"
                                        id="DelinquencyHistory.rowTitle"
                                        values={{
                                            cycleNumber:
                                                bucket?.statement_cycle,
                                        }}
                                    />
                                }
                                primarySubtitle={
                                    <Box component="span">
                                        <DateFormatter
                                            date={bucket?.created_at}
                                            format="fullDateAbr"
                                        />
                                        {' • '}
                                        <FormattedMessage
                                            id="DelinquencyHistory.bucketAmount"
                                            defaultMessage="Bucket amount: {value}"
                                            values={{
                                                value: formatCurrency({
                                                    formatNumber,
                                                    currency:
                                                        program?.currency_numeric_code,
                                                    amount: bucket?.bucket_amount,
                                                }),
                                            }}
                                        />
                                        {' • '}
                                        <FormattedMessage
                                            id="DelinquencyHistory.bucketBalance"
                                            defaultMessage="Bucket balance: {value}"
                                            values={{
                                                value: formatCurrency({
                                                    formatNumber,
                                                    currency:
                                                        program?.currency_numeric_code,
                                                    amount: bucket?.bucket_balance,
                                                }),
                                            }}
                                        />
                                    </Box>
                                }
                                secondaryTitle={
                                    <>
                                        <DelinquencyChips
                                            delinquencyBucket={bucket}
                                        />
                                        <Box sx={{ ml: 1 }} component="span">
                                            <CurrencyFormatter
                                                amount={
                                                    bucket?.calculation_memory
                                                        ?.overdue_amount
                                                }
                                                currency={
                                                    program?.currency_numeric_code
                                                }
                                            />
                                        </Box>
                                    </>
                                }
                            />
                        );
                    })}
                </InfiniteScroll>
            </Card>
        </>
    );
};

export default DelinquencyHistory;
